import React, { useState } from 'react';
import '../styles/Dashboard.css';

function AccountModal({ isOpen, onClose, onExportSuccess, onExportError }) {
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('kinde_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Download the ZIP file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'certifiable-data-export.zip';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      if (onExportSuccess) {
        onExportSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Export error:', error);
      if (onExportError) {
        onExportError();
      }
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem('kinde_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Deletion failed');
      }

      alert('Your account has been deleted. You will now be logged out.');
      
      // Clear local storage and redirect to logout
      localStorage.clear();
      window.location.href = '/api/auth/logout';
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete account. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    setShowDeleteConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '540px' }}>
        <div className="modal-header">
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--ink)', margin: 0 }}>
            Account Management
          </h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body" style={{ paddingTop: '24px' }}>
          {/* Export Data Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: 'var(--ink)', 
              marginBottom: '12px' 
            }}>
              Export Your Data
            </h3>
            <p style={{ 
              fontSize: '14px', 
              color: 'var(--muted)', 
              marginBottom: '16px',
              lineHeight: '1.5'
            }}>
              Download all your certificates and data as a ZIP file. This includes all uploaded files and certificate information.
            </p>
            <button
              type="button"
              onClick={handleExportData}
              disabled={exporting}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid var(--primary)',
                borderRadius: '8px',
                background: 'white',
                color: 'var(--primary)',
                fontSize: '14px',
                fontWeight: '600',
                cursor: exporting ? 'not-allowed' : 'pointer',
                opacity: exporting ? 0.6 : 1,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '18px' }}>📦</span>
              {exporting ? 'Exporting...' : 'Export My Data'}
            </button>
          </div>

          {/* Delete Account Section */}
          <div style={{ 
            paddingTop: '24px', 
            borderTop: '2px solid var(--line)'
          }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: 'var(--ink)', 
              marginBottom: '12px' 
            }}>
              Delete Account
            </h3>

            {!showDeleteConfirm ? (
              <>
                <p style={{ 
                  fontSize: '14px', 
                  color: 'var(--muted)', 
                  marginBottom: '16px',
                  lineHeight: '1.5'
                }}>
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #dc2626',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#dc2626',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>⚠️</span>
                  Delete My Account
                </button>
              </>
            ) : (
              <div style={{
                background: '#fef2f2',
                border: '2px solid #dc2626',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <p style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#991b1b',
                  marginBottom: '12px'
                }}>
                  ⚠️ Are you absolutely sure?
                </p>
                <p style={{
                  fontSize: '13px',
                  color: '#7f1d1d',
                  marginBottom: '16px',
                  lineHeight: '1.5'
                }}>
                  This will permanently delete your account, all certificates, and uploaded files. This action cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      background: 'white',
                      color: 'var(--text)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      opacity: deleting ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      borderRadius: '8px',
                      background: '#dc2626',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      opacity: deleting ? 0.6 : 1
                    }}
                  >
                    {deleting ? 'Deleting...' : 'Yes, Delete Everything'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {!showDeleteConfirm && (
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountModal;
